import { useState } from 'react';
import { Megaphone, Check } from 'lucide-react';
import { useDepartments } from '@/api/queries/employee.queries';
import { PageHeader } from '@/components/shared/PageHeader';
import { Button } from '@/components/ui/button';

export default function BroadcastPage() {
  const { data: deptsResp } = useDepartments();
  const departments = deptsResp?.data || [];
  const [target, setTarget] = useState('all');
  const [deptId, setDeptId] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSend = async () => {
    if (!message.trim()) return;
    setSending(true);
    await new Promise(r => setTimeout(r, 1200));
    setSent(true);
    setSending(false);
    setTimeout(() => { setSent(false); setMessage(''); }, 3000);
  };

  return (
    <div className="max-w-xl space-y-5 fade-in">
      <PageHeader
        title="Broadcast Message"
        subtitle="Send a message to all employees or a specific department"
        breadcrumb={[{ label: 'Chat', href: '/chat' }, { label: 'Broadcast' }]}
      />

      <div className="card space-y-5 p-5">
        {sent ? (
          <div className="flex flex-col items-center py-8 gap-3">
            <div className="w-14 h-14 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
              <Check size={28} className="text-green-600"/>
            </div>
            <p className="text-[16px] font-bold text-[var(--text-primary)]">Broadcast Sent!</p>
            <p className="text-[13px] text-[var(--text-secondary)]">
              {target === 'all' ? 'Sent to all employees' : `Sent to ${departments.find(d => d.id === deptId)?.name || 'department'}`}
            </p>
          </div>
        ) : (
          <>
            <div className="space-y-2">
              <p className="text-[13px] font-semibold text-[var(--text-primary)]">Send to</p>
              <div className="flex gap-2">
                <button onClick={() => setTarget('all')}
                  className={`px-4 py-2 rounded-[8px] text-[13px] font-semibold transition-colors ${target === 'all' ? 'bg-[var(--accent)] text-white' : 'bg-[var(--bg-muted)] text-[var(--text-secondary)]'}`}>
                  All Employees
                </button>
                <button onClick={() => setTarget('dept')}
                  className={`px-4 py-2 rounded-[8px] text-[13px] font-semibold transition-colors ${target === 'dept' ? 'bg-[var(--accent)] text-white' : 'bg-[var(--bg-muted)] text-[var(--text-secondary)]'}`}>
                  Department
                </button>
              </div>
            </div>

            {target === 'dept' && (
              <div className="space-y-1.5">
                <label className="text-[13px] font-semibold text-[var(--text-primary)]">Select Department</label>
                <select value={deptId} onChange={e => setDeptId(e.target.value)}
                  className="w-full h-10 px-3 bg-[var(--bg-muted)] border border-[var(--border)] rounded-[10px] text-[14px] text-[var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--accent)]">
                  <option value="">Select…</option>
                  {departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                </select>
              </div>
            )}

            <div className="space-y-1.5">
              <label className="text-[13px] font-semibold text-[var(--text-primary)]">Message</label>
              <textarea
                value={message}
                onChange={e => setMessage(e.target.value)}
                placeholder="Type your broadcast message…"
                rows={5}
                className="w-full px-3 py-2.5 bg-[var(--bg-muted)] border border-[var(--border)] rounded-[10px] text-[14px] text-[var(--text-primary)] placeholder:text-[var(--text-tertiary)] focus:outline-none focus:ring-2 focus:ring-[var(--accent)] resize-none"
              />
              <p className="text-[11px] text-[var(--text-tertiary)]">{message.length}/500 characters</p>
            </div>

            <div className="p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-[10px]">
              <p className="text-[12px] text-amber-700 dark:text-amber-400 font-medium">Phase 1 — Test Mode</p>
              <p className="text-[12px] text-amber-600 dark:text-amber-500 mt-0.5">Push notifications via FCM and WhatsApp via WATI activate in Phase 2.</p>
            </div>

            <Button onClick={handleSend} loading={sending} icon={Megaphone} disabled={!message.trim() || (target === 'dept' && !deptId)}>
              Send Broadcast
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
